// src/components/signals/SignalCatalogCard.tsx
// One card per deterministic signal type: description, the datasets it uses,
// and its utilization stats. Read-only. Colors/fonts come from design tokens.
import { Card, CardContent } from "@/components/ui/card";
import type { SignalCatalogEntry } from "@/lib/signal-catalog";
import type { SignalTypeStats } from "@/lib/signal-stats";

function StatBox({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-md border border-border/60 bg-muted/40 px-3 py-2">
      <div className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="mt-0.5 font-mono text-lg font-semibold text-foreground">{value}</div>
    </div>
  );
}

function pct(rate: number): string {
  return `${Math.round(rate * 100)}%`;
}

export function SignalCatalogCard({
  entry,
  stats,
}: {
  entry: SignalCatalogEntry;
  stats: SignalTypeStats | undefined;
}) {
  return (
    <Card className="border-border/50">
      <CardContent className="space-y-4 p-5">
        <div>
          <h3 className="font-display text-lg font-semibold text-foreground">{entry.title}</h3>
          <p className="mt-1 text-sm text-muted-foreground">{entry.description}</p>
        </div>

        <div>
          <div className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
            Datasets used
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            {entry.datasets.map((d) => (
              <span
                key={d}
                className="rounded-full border border-border bg-muted px-2.5 py-1 text-xs text-foreground"
              >
                {d}
              </span>
            ))}
          </div>
        </div>

        {stats ? (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              <StatBox label="Open" value={stats.open} />
              <StatBox label="Confirmed" value={stats.confirmed} />
              <StatBox label="Dismissed" value={stats.dismissed} />
              <StatBox label="Auto-resolved" value={stats.resolvedBySystem} />
            </div>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              <StatBox label="Total raised" value={stats.totalRaised} />
              <StatBox label="Raised (window)" value={stats.raisedInWindow} />
              <StatBox label="Resolved (window)" value={stats.resolvedInWindow} />
            </div>
            <div>
              <div className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
                Outcome of resolved ({stats.totalResolved})
              </div>
              <div className="mt-2 grid grid-cols-3 gap-2 text-sm">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-muted-foreground">Confirmed</span>
                  <span className="font-mono font-medium text-foreground">
                    {pct(stats.confirmedRate)}
                  </span>
                </div>
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-muted-foreground">Dismissed</span>
                  <span className="font-mono font-medium text-foreground">
                    {pct(stats.dismissedRate)}
                  </span>
                </div>
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-muted-foreground">Auto</span>
                  <span className="font-mono font-medium text-foreground">
                    {pct(stats.autoResolvedRate)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground/70 italic">No statistics available.</p>
        )}
      </CardContent>
    </Card>
  );
}
